import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Arrow from 'components/Icons/ArrowIcon';
import { ImageGallery } from 'shared-ui';
import RectangleSkeleton from 'components/Skeletons/RectangleSkeleton';
import WidgetErrorMessage from 'components/WidgetErrorMessage/WidgetErrorMessage';
import WidgetBox from 'components/widgets/WidgetBox';
import { trackingClient } from 'clients';
import { HTTPError } from 'clients/HTTPClient';
import { getMockRecentCameras } from 'mocks/recentCameras';
import { CameraOrientation, RecentCameraImages } from 'types/CarTracking';
import {
    CAMERA_IMAGES_EMPTY_MESSAGE,
    CAMERA_TOP_SECTION_LAYOUT_CONTEXTS,
    CAMERA_VIEW_RENDER_MODES,
    CameraTopSectionLayoutContext,
    getCameraViewLayoutVariant,
    getCameraViewRenderMode,
    getTripwireFeeds,
    toCarouselImages,
} from './cameraViewHelpers';
import CameraViewAllModal from './CameraViewAllModal';
import styles from './CameraViewWidget.module.css';

type CameraViewWidgetProps = {
    spotUuid: string;
    isDemoMode?: boolean;
    showSearchAndFilterControls?: boolean;
    topSectionLayoutContext?: CameraTopSectionLayoutContext;
};

function CameraViewLoadingSkeleton(): JSX.Element {
    return (
        <div className={styles.skeletonContainer} data-testid="camera-view-skeleton">
            <RectangleSkeleton />
        </div>
    );
}

function CameraViewWidget({
    spotUuid,
    isDemoMode = false,
    showSearchAndFilterControls = false,
    topSectionLayoutContext = CAMERA_TOP_SECTION_LAYOUT_CONTEXTS.SPLIT,
}: CameraViewWidgetProps): JSX.Element {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [report, setReport] = useState<RecentCameraImages | null>(null);
    const [isViewAllOpen, setIsViewAllOpen] = useState(false);

    const fetchRecentCameraImages = useCallback(async () => {
        if (!spotUuid) {
            return;
        }

        setLoading(true);
        setErrorMessage('');
        try {
            const response = isDemoMode
                ? getMockRecentCameras(spotUuid, 'mixed')
                : await trackingClient.getRecentCameraImages(spotUuid);
            setReport(response);
        } catch (error) {
            if (HTTPError.isHTTPError(error)) {
                setErrorMessage(error.message);
            } else {
                setErrorMessage(error?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [isDemoMode, spotUuid]);

    useEffect(() => {
        fetchRecentCameraImages();
    }, [fetchRecentCameraImages]);

    const aerialImages = useMemo(() => toCarouselImages(report?.overhead_images ?? [], 'Aerial'), [report]);
    const entranceImages = useMemo(
        () =>
            toCarouselImages(
                (report?.lpr_images ?? []).filter((image) => image.orientation === CameraOrientation.ENTRANCE),
                'Entrance'
            ),
        [report]
    );
    const exitImages = useMemo(
        () =>
            toCarouselImages(
                (report?.lpr_images ?? []).filter((image) => image.orientation === CameraOrientation.EXIT),
                'Exit'
            ),
        [report]
    );

    const hasAerialImages = aerialImages.length > 0;
    const hasEntranceImages = entranceImages.length > 0;
    const hasExitImages = exitImages.length > 0;
    const hasTripwireImages = hasEntranceImages || hasExitImages;
    const tripwireFeeds = useMemo(() => getTripwireFeeds(entranceImages, exitImages), [entranceImages, exitImages]);
    const layoutVariant = getCameraViewLayoutVariant(hasAerialImages, hasTripwireImages);
    const renderMode = getCameraViewRenderMode(layoutVariant, topSectionLayoutContext);

    const isLoading = loading || !report;
    const shouldRenderEmptyState = !!report && !hasAerialImages && !hasTripwireImages;

    const content = useMemo(() => {
        if (errorMessage) {
            return (
                <div className={styles.centeredContainer} data-testid="camera-view-widget">
                    <WidgetErrorMessage message={errorMessage} onRetry={fetchRecentCameraImages} />
                </div>
            );
        }

        if (isLoading) {
            return (
                <div className={styles.centeredContainer} data-testid="camera-view-widget">
                    <CameraViewLoadingSkeleton />
                </div>
            );
        }

        if (shouldRenderEmptyState) {
            return <p className={styles.emptyState}>{CAMERA_IMAGES_EMPTY_MESSAGE}</p>;
        }

        if (renderMode === CAMERA_VIEW_RENDER_MODES.AERIAL_ONLY) {
            return (
                <div className={`${styles.cameraLayout} ${styles.aerialOnly}`} data-testid="camera-view-layout">
                    <div className={styles.aerialFeed} data-testid="camera-view-aerial-feed">
                        <ImageGallery key="camera-view-aerial" images={aerialImages} isExpandable />
                    </div>
                </div>
            );
        }

        if (renderMode === CAMERA_VIEW_RENDER_MODES.MIXED_SPLIT) {
            return (
                <div className={`${styles.cameraLayout} ${styles.mixedSplit}`} data-testid="camera-view-layout">
                    <div className={styles.aerialFeed} data-testid="camera-view-aerial-feed">
                        <ImageGallery key="camera-view-aerial" images={aerialImages} isExpandable />
                    </div>
                    <div
                        className={`${styles.tripwireRow} ${tripwireFeeds.length === 1 ? styles.tripwireSingle : ''}`}
                        data-testid="camera-view-tripwire-row"
                    >
                        {tripwireFeeds.map((feed) => (
                            <div key={feed.key} className={styles.tripwireFeed}>
                                <ImageGallery key={`camera-view-${feed.key}`} images={feed.images} isExpandable />
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (renderMode === CAMERA_VIEW_RENDER_MODES.MIXED_FULL_WIDTH) {
            return (
                <div className={`${styles.cameraLayout} ${styles.mixedFullWidth}`} data-testid="camera-view-layout">
                    <div className={styles.aerialFeed} data-testid="camera-view-aerial-feed">
                        <ImageGallery key="camera-view-aerial" images={aerialImages} isExpandable />
                    </div>
                    <div
                        className={`${styles.tripwireColumn} ${
                            tripwireFeeds.length === 1 ? styles.tripwireSingle : ''
                        }`}
                        data-testid="camera-view-tripwire-column"
                    >
                        {tripwireFeeds.map((feed) => (
                            <div key={feed.key} className={styles.tripwireFeed}>
                                <ImageGallery key={`camera-view-${feed.key}`} images={feed.images} isExpandable />
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (renderMode === CAMERA_VIEW_RENDER_MODES.TRIPWIRE_ROW) {
            return (
                <div
                    className={`${styles.cameraLayout} ${styles.tripwireOnlyFullWidth}`}
                    data-testid="camera-view-layout"
                >
                    <div
                        className={`${styles.tripwireRow} ${tripwireFeeds.length === 1 ? styles.tripwireSingle : ''}`}
                        data-testid="camera-view-tripwire-row"
                    >
                        {tripwireFeeds.map((feed) => (
                            <div key={feed.key} className={styles.tripwireFeed}>
                                <ImageGallery key={`camera-view-${feed.key}`} images={feed.images} isExpandable />
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <div className={`${styles.cameraLayout} ${styles.tripwireOnlySplit}`} data-testid="camera-view-layout">
                <div
                    className={`${styles.tripwireColumn} ${tripwireFeeds.length === 1 ? styles.tripwireSingle : ''}`}
                    data-testid="camera-view-tripwire-column"
                >
                    {tripwireFeeds.map((feed) => (
                        <div key={feed.key} className={styles.tripwireFeed}>
                            <ImageGallery key={`camera-view-${feed.key}`} images={feed.images} isExpandable />
                        </div>
                    ))}
                </div>
            </div>
        );
    }, [
        aerialImages,
        errorMessage,
        fetchRecentCameraImages,
        isLoading,
        renderMode,
        shouldRenderEmptyState,
        tripwireFeeds,
    ]);

    return (
        <>
            <WidgetBox
                title="Camera View"
                rightElement={
                    <button
                        type="button"
                        className={styles.viewAll}
                        data-testid="camera-view-view-all"
                        onClick={() => setIsViewAllOpen(true)}
                    >
                        View All <Arrow direction="right" color="#1c2024" />
                    </button>
                }
            >
                <div className={styles.widgetBody} data-testid="camera-view-widget">
                    {content}
                </div>
            </WidgetBox>
            {isViewAllOpen ? (
                <CameraViewAllModal
                    spotUuid={spotUuid}
                    isDemoMode={isDemoMode}
                    showSearchAndFilterControls={showSearchAndFilterControls}
                    onClose={() => setIsViewAllOpen(false)}
                />
            ) : null}
        </>
    );
}

export default CameraViewWidget;
